import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Search, Receipt, DollarSign, Check, Clock, 
  ChevronRight, Image as ImageIcon
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import AddReimbursementDialog from './AddReimbursementDialog';
import ReimbursementDetailSheet from './ReimbursementDetailSheet';
import { format, parseISO } from 'date-fns';
import { getFileUrl } from '../../utils/fileStorage';
import { useDemo } from '@/contexts/DemoContext';

const ReimbursementsTab = () => {
  const { isDemoMode, getReimbursements } = useDemo();
  const [reimbursements, setReimbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [summary, setSummary] = useState({ outstanding_total: 0, reimbursed_total: 0 });

  useEffect(() => {
    fetchReimbursements();
    fetchSummary();
  }, [isDemoMode]);

  const fetchReimbursements = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const demoReimbursements = getReimbursements() || [];
        setReimbursements(demoReimbursements);
        setLoading(false);
        return;
      }
      
      const response = await API.get('/reimbursements');
      setReimbursements(response.data || []);
    } catch (error) {
      console.error('Failed to fetch reimbursements:', error);
      if (!isDemoMode) {
        toast.error('Failed to load reimbursements');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (isDemoMode) {
      // Calculate summary from demo data
      const demoReimbursements = getReimbursements() || [];
      const outstanding = demoReimbursements
        .filter(r => r.status !== 'reimbursed')
        .reduce((sum, r) => sum + (r.amount || 0), 0);
      const reimbursed = demoReimbursements
        .filter(r => r.status === 'reimbursed')
        .reduce((sum, r) => sum + (r.amount || 0), 0);
      setSummary({ outstanding_total: outstanding, reimbursed_total: reimbursed });
      return;
    }
    
    try {
      const response = await API.get('/reimbursements/summary/totals');
      setSummary(response.data || { outstanding_total: 0, reimbursed_total: 0 });
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleReimbursementAdded = () => {
    fetchReimbursements();
    fetchSummary();
    setShowAddDialog(false);
    toast.success('Reimbursement added successfully');
  };

  const handleReimbursementUpdated = () => {
    fetchReimbursements();
    fetchSummary();
    setSelectedReimbursement(null);
    toast.success('Reimbursement updated');
  };

  const handleDeleteReimbursement = async (reimbursementId) => {
    try {
      await API.delete(`/reimbursements/${reimbursementId}`);
      fetchReimbursements();
      fetchSummary();
      setSelectedReimbursement(null);
      toast.success('Reimbursement deleted');
    } catch (error) {
      toast.error('Failed to delete reimbursement');
    }
  };

  const filteredReimbursements = reimbursements.filter(r =>
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const outstanding = filteredReimbursements.filter(r => r.status === 'outstanding');
  const reimbursed = filteredReimbursements.filter(r => r.status === 'reimbursed');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reimbursements-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-700">Outstanding</span>
            </div>
            <p className="text-2xl font-bold text-amber-700 mt-1">
              {formatCurrency(summary.outstanding_total)}
            </p>
            <p className="text-xs text-amber-600">{summary.outstanding_count || 0} items</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-700">Reimbursed</span>
            </div>
            <p className="text-2xl font-bold text-green-700 mt-1">
              {formatCurrency(summary.reimbursed_total)}
            </p>
            <p className="text-xs text-green-600">{summary.reimbursed_count || 0} items</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="bg-pink-500 hover:bg-pink-600"
          data-testid="add-reimbursement-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Reimbursement
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search reimbursements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-white/50"
          data-testid="search-reimbursements"
        />
      </div>

      {/* Outstanding Section */}
      {outstanding.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-slate-700 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            Outstanding ({outstanding.length})
          </h3>
          <div className="grid gap-2">
            {outstanding.map((reimbursement) => (
              <Card 
                key={reimbursement.id}
                className="cursor-pointer hover:shadow-md transition-shadow border-amber-100"
                onClick={() => setSelectedReimbursement(reimbursement)}
                data-testid={`reimbursement-card-${reimbursement.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Receipt Image Thumbnail */}
                    <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {reimbursement.receipt_image ? (
                        <img 
                          src={getFileUrl(reimbursement.receipt_image)} 
                          alt="Receipt" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Receipt className="h-6 w-6 text-amber-400" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-800 truncate">
                        {reimbursement.description}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {format(parseISO(reimbursement.date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-right">
                      <p className="font-semibold text-amber-700">
                        {formatCurrency(reimbursement.amount)}
                      </p>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        Outstanding
                      </Badge>
                    </div>
                    
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Reimbursed Section */}
      {reimbursed.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-slate-700 flex items-center gap-2">
            <Check className="h-4 w-4 text-green-600" />
            Reimbursed ({reimbursed.length})
          </h3>
          <div className="grid gap-2">
            {reimbursed.map((reimbursement) => (
              <Card 
                key={reimbursement.id}
                className="cursor-pointer hover:shadow-md transition-shadow border-green-100 opacity-75"
                onClick={() => setSelectedReimbursement(reimbursement)}
                data-testid={`reimbursement-card-${reimbursement.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Receipt Image Thumbnail */}
                    <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {reimbursement.receipt_image ? (
                        <img 
                          src={getFileUrl(reimbursement.receipt_image)} 
                          alt="Receipt" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Receipt className="h-6 w-6 text-green-400" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-800 truncate">
                        {reimbursement.description}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {format(parseISO(reimbursement.date), 'MMM d, yyyy')}
                        {reimbursement.date_reimbursed && (
                          <span className="ml-2 text-green-600">
                            (Paid: {format(parseISO(reimbursement.date_reimbursed), 'MMM d')})
                          </span>
                        )}
                      </p>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-right">
                      <p className="font-semibold text-green-700">
                        {formatCurrency(reimbursement.amount)}
                      </p>
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        Reimbursed
                      </Badge>
                    </div>
                    
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {outstanding.length === 0 && reimbursed.length === 0 && (
        <div className="text-center py-12">
          <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium mb-2">Track Reimbursements</p>
          <p className="text-sm text-slate-400 mb-4">
            Keep track of receipts and expenses you need to get reimbursed for
          </p>
          <Button 
            onClick={() => setShowAddDialog(true)}
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Receipt
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <AddReimbursementDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onReimbursementAdded={handleReimbursementAdded}
      />

      <ReimbursementDetailSheet
        reimbursement={selectedReimbursement}
        open={!!selectedReimbursement}
        onOpenChange={(open) => !open && setSelectedReimbursement(null)}
        onReimbursementUpdated={handleReimbursementUpdated}
        onReimbursementDeleted={() => handleDeleteReimbursement(selectedReimbursement?.id)}
      />
    </div>
  );
};

export default ReimbursementsTab;
